import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Upload, X, ImageIcon, Video, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { validateUpload, limitHint } from "@/lib/uploadLimits";

export async function uploadFile(file, purpose = "general") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("purpose", purpose);
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
  });
}

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9));
}

// Square/aspect image uploader with crop.
export function ImageUploader({ value, onChange, aspect = 1, label, hint, testId, rounded = "rounded-xl", heightClass = "h-32 w-32" }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const onSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const chk = validateUpload(f, "image");
    if (!chk.ok) { toast.error(chk.message); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = () => { setSrc(reader.result); setOpen(true); setZoom(1); setCrop({ x: 0, y: 0 }); };
    reader.readAsDataURL(f);
    e.target.value = "";
  };
  const onComplete = useCallback((_, areaPixels) => setPixels(areaPixels), []);
  const save = async () => {
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, pixels);
      const url = await uploadFile(new File([blob], "image.jpg", { type: "image/jpeg" }), "image");
      onChange(url);
      setOpen(false);
    } catch {
      toast.error("Could not upload image.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testId}>
      {label && <Label className="mb-1.5 block">{label}</Label>}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onSelect} />
      {value ? (
        <div className={cn("group relative", aspect !== 1 ? "block w-full" : "inline-block")}>
          <img src={fileUrl(value)} alt={label} className={cn("border border-border object-cover", rounded, heightClass)} style={aspect !== 1 ? { width: "100%", height: "auto", aspectRatio: aspect } : {}} />
          <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-xl bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <Button type="button" size="sm" variant="secondary" onClick={() => inputRef.current?.click()}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button type="button" size="sm" variant="destructive" onClick={() => onChange(null)}><X className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()}
          className={cn("flex flex-col items-center justify-center gap-1.5 border border-dashed border-border bg-secondary/40 text-muted-foreground hover:bg-secondary", rounded, aspect === 1 ? heightClass : "w-full")}
          style={aspect !== 1 ? { aspectRatio: aspect } : {}}>
          <ImageIcon className="h-5 w-5" />
          <span className="text-xs">Upload</span>
          {hint && <span className="text-[11px] text-muted-foreground" data-testid={`${testId}-hint`}>{hint}</span>}
          <span className="text-[10px] text-muted-foreground/70">{limitHint("image")}</span>
        </button>
      )}
      {hint && value && <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid={`${testId}-hint`}>{hint}</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Adjust image</DialogTitle></DialogHeader>
          <div className="relative h-72 w-full overflow-hidden rounded-lg bg-black">
            {src && <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />}
          </div>
          <div className="px-1">
            <Label className="text-xs text-muted-foreground">Zoom</Label>
            <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={(v) => setZoom(v[0])} className="mt-2" />
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="button" onClick={save} disabled={busy} data-testid={`${testId}-save`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Cover image — defaults to 16:9 (resource/event cards & detail hero).
// Profiles (personal/business/capital) pass aspect={4} for the original 4:1 banner crop.
export function CoverUploader({ value, onChange, testId, aspect = 16 / 9, hint }) {
  const wide = aspect === 4;
  const sizing = hint || `Recommended ${wide ? "2000 × 500 px" : "1600 × 900 px"} • ${wide ? "4:1" : "16:9"} ratio`;
  return <ImageUploader value={value} onChange={onChange} aspect={aspect} hint={sizing} testId={testId} rounded="rounded-xl" />;
}

// Intro video: upload a file OR paste a YouTube URL. value = {type, url}.
export function VideoField({ value, onChange, testId }) {
  const v = value || { type: "youtube", url: "" };
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const onSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const chk = validateUpload(f, "video");
    if (!chk.ok) { toast.error(chk.message); e.target.value = ""; return; }
    setBusy(true);
    try {
      const url = await uploadFile(f, "video");
      onChange({ type: "upload", url });
    } catch { toast.error("Video upload failed."); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div data-testid={testId}>
      <Tabs value={v.type} onValueChange={(t) => onChange({ type: t, url: "" })}>
        <TabsList className="grid w-full max-w-xs grid-cols-2">
          <TabsTrigger value="youtube">YouTube</TabsTrigger>
          <TabsTrigger value="upload">Upload</TabsTrigger>
        </TabsList>
        <TabsContent value="youtube" className="mt-3">
          <Input placeholder="https://youtube.com/watch?v=…" value={v.url || ""} onChange={(e) => onChange({ type: "youtube", url: e.target.value })} data-testid={`${testId}-youtube`} />
        </TabsContent>
        <TabsContent value="upload" className="mt-3">
          <input ref={inputRef} type="file" accept="video/*" className="hidden" onChange={onSelect} />
          {v.url && v.type === "upload" ? (
            <div className="flex items-center gap-3">
              <video src={fileUrl(v.url)} className="h-24 rounded-lg border border-border" controls />
              <Button type="button" variant="secondary" size="sm" onClick={() => onChange({ type: "upload", url: "" })}>Remove</Button>
            </div>
          ) : (
            <div className="flex flex-col items-start gap-1">
              <Button type="button" variant="secondary" onClick={() => inputRef.current?.click()} disabled={busy}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Video className="mr-2 h-4 w-4" />} Upload video
              </Button>
              <span className="text-[10px] text-muted-foreground/70">{limitHint("video")}</span>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
